/**
 * 和星海服务说话。设备令牌放在 localStorage；照片和数据都以服务端为准，缩略图和上次同步的快照
 * 另有一份可以随时清空的离线缓存（offline.ts）。
 * 实时推送用 fetch 读 SSE：EventSource 不能带令牌头，令牌也不该出现在网址里。
 */
import {
  API,
  capsulePath,
  invitePath,
  memoryPath,
  memoryThemePath,
  noteReadPath,
  perspectivePath,
  photoItemPath,
  photoPath,
  restorePath,
  themePath,
  trashItemPath,
  type ApiErrorBody,
  type CapsulePatch,
  type CapsuleResponse,
  type Change,
  type DevicesResponse,
  type HealthResponse,
  type InvitePreviewResponse,
  type InviteRequest,
  type InviteResponse,
  type InvitesResponse,
  type LocalDate,
  type MePatch,
  type MeResponse,
  type MemoryPatch,
  type MemoryResponse,
  type MemoryThemesResponse,
  type NoteResponse,
  type PairRequest,
  type PerspectivePatch,
  type PerspectivesResponse,
  type PhotoVariant,
  type PresenceResponse,
  type RestoreResponse,
  type SessionResponse,
  type SetupRequest,
  type SnapshotResponse,
  type SpacePatch,
  type SpaceResponse,
  type ThemeResponse,
  type TrashResponse,
  type TrashedPhotoResponse,
  type UploadResponse,
} from '@star-sea/shared';
import { FetchError, thumbFetch } from './offline.ts';

const TOKEN_KEY = 'star-sea/token';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** 服务端返回的整个错误体（改回忆撞上 Ta 刚改过时，里面有最新的回忆） */
  readonly body: unknown;
  constructor(status: number, code: string, message: string, body?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

function loadToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export type StreamState = 'open' | 'retrying' | 'unauthorized';

export class Api {
  token: string | null = loadToken();

  setToken(token: string | null): void {
    this.token = token;
    try {
      if (token) localStorage.setItem(TOKEN_KEY, token);
      else localStorage.removeItem(TOKEN_KEY);
    } catch {
      // 隐私模式下存不了，这次会话里照样能用
    }
  }

  private headers(extra: Record<string, string> = {}): Record<string, string> {
    return this.token ? { ...extra, authorization: `Bearer ${this.token}` } : extra;
  }

  private async request<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
    const { json, ...rest } = init;
    let res: Response;
    try {
      res = await fetch(path, {
        ...rest,
        headers: this.headers(json === undefined ? {} : { 'content-type': 'application/json' }),
        body: json === undefined ? rest.body : JSON.stringify(json),
      });
    } catch {
      throw new ApiError(0, 'network', '连不上星海服务');
    }
    if (!res.ok) {
      let body: Partial<ApiErrorBody> = {};
      try {
        body = (await res.json()) as ApiErrorBody;
      } catch {
        // 不是 JSON（比如代理返回的错误页）
      }
      throw new ApiError(res.status, body.error ?? 'internal', body.message ?? `请求失败（${res.status}）`, body);
    }
    return (res.status === 204 ? undefined : await res.json()) as T;
  }

  health = () => this.request<HealthResponse>(API.health);
  setup = (req: SetupRequest) => this.request<SessionResponse>(API.setup, { method: 'POST', json: req });
  pair = (req: PairRequest) => this.request<SessionResponse>(API.pair, { method: 'POST', json: req });
  me = () => this.request<MeResponse>(API.me);
  /** 改自己的资料（生日） */
  updateMe = (patch: MePatch) => this.request<MeResponse>(API.me, { method: 'PATCH', json: patch });
  snapshot = () => this.request<SnapshotResponse>(API.snapshot);
  /** 生成邀请码：邀请 Ta 时可以附一张星海里的照片和一句话（photoId、message 只对邀请 Ta 有效，ADR-0013） */
  invite = (req: InviteRequest) => this.request<InviteResponse>(API.invites, { method: 'POST', json: req });
  /** 我发出的、还没用掉也没撤回的邀请（码本身只在生成时给一次，这里拿不到） */
  invites = () => this.request<InvitesResponse>(API.invites);
  /** 撤回一个我发出的邀请 */
  revokeInvite = (id: string) => this.request<void>(invitePath(id), { method: 'DELETE' });
  /** 不登录也能调（ADR-0013）：看邀请附的那张照片和那句话；码放在请求体里，不进网址和访问日志 */
  previewInvite = (code: string) => this.request<InvitePreviewResponse>(API.invitePreview, { method: 'POST', json: { code } });
  /** 对方 24 小时内来过没有（ADR-0013；P3b 用） */
  presence = () => this.request<PresenceResponse>(API.presence);
  devices = () => this.request<DevicesResponse>(API.devices);
  /** 撞上 Ta 刚改过时抛 409 的 ApiError，body 是 MemoryConflictBody */
  updateMemory = (id: string, patch: MemoryPatch) => this.request<MemoryResponse>(memoryPath(id), { method: 'PATCH', json: patch });
  updateSpace = (patch: SpacePatch) => this.request<SpaceResponse>(API.space, { method: 'PATCH', json: patch });
  /** 放进回收站 */
  deletePhoto = (id: string) => this.request<TrashedPhotoResponse>(photoItemPath(id), { method: 'DELETE' });
  restorePhoto = (id: string) => this.request<RestoreResponse>(restorePath(id), { method: 'POST' });
  trash = () => this.request<TrashResponse>(API.trash);
  /** 永久删除（只能删回收站里的） */
  purgePhoto = (id: string) => this.request<void>(trashItemPath(id), { method: 'DELETE' });
  /** 写自己在这张照片上的那一面；另一台设备刚改过时抛 409 的 ApiError，body 是 PerspectiveConflictBody */
  writePerspective = (photoId: string, patch: PerspectivePatch) => this.request<PerspectivesResponse>(perspectivePath(photoId), { method: 'PUT', json: patch });
  // 时间胶囊（ADR-0011）
  /** 封存：表单里有形态、信、解封的时刻和那天，照片可选（字段同上传） */
  sealCapsule = (form: FormData) => this.request<CapsuleResponse>(API.capsules, { method: 'POST', body: form });
  updateCapsule = (id: string, patch: CapsulePatch) => this.request<CapsuleResponse>(capsulePath(id), { method: 'PATCH', json: patch });
  peekCapsule = (id: string) => this.request<CapsuleResponse>(capsulePath(id, 'peek'), { method: 'POST' });
  openCapsule = (id: string) => this.request<CapsuleResponse>(capsulePath(id, 'open'), { method: 'POST' });
  /** 撤回；keepPhoto 时里面的照片转成普通照片 */
  removeCapsule = (id: string, keepPhoto: boolean) => this.request<void>(`${capsulePath(id)}${keepPhoto ? '?photo=keep' : ''}`, { method: 'DELETE' });
  // 入场暗号（ADR-0011）
  /** 写、改留给 Ta 的那句（Ta 还没看到的那条）；text 是 null 时撤回。date 是这台设备那里的今天 */
  writeNote = (text: string | null, date: LocalDate) => this.request<NoteResponse>(API.myNote, { method: 'PUT', json: { text, date } });
  /** 入场时看到了 Ta 留的那句 */
  readNote = (id: string) => this.request<NoteResponse>(noteReadPath(id), { method: 'POST' });
  // 主题星座（ADR-0011）
  /** 建主题；重名时抛 409 的 ApiError */
  createTheme = (name: string) => this.request<ThemeResponse>(API.themes, { method: 'POST', json: { name } });
  renameTheme = (id: string, name: string) => this.request<ThemeResponse>(themePath(id), { method: 'PATCH', json: { name } });
  /** 删主题：它也从所有回忆上摘掉 */
  deleteTheme = (id: string) => this.request<void>(themePath(id), { method: 'DELETE' });
  /** 给回忆打上（on）或去掉主题，回来的是它现在的全部主题 */
  tagMemory = (memoryId: string, themeId: string, on: boolean) => this.request<MemoryThemesResponse>(memoryThemePath(memoryId, themeId), { method: on ? 'PUT' : 'DELETE' });

  /** 缩略图先查离线缓存（offline.ts）；原图和 2048 档每次都联网取 */
  async photoBlob(id: string, variant: PhotoVariant, signal?: AbortSignal): Promise<Blob> {
    if (variant === 't128' || variant === 't512') {
      try {
        return await thumbFetch(photoPath(id, variant), this.token, signal);
      } catch (e) {
        if (e instanceof FetchError) throw new ApiError(e.status, e.status ? 'not_found' : 'network', e.status ? '照片取不到' : '连不上星海服务');
        throw e;
      }
    }
    const res = await fetch(photoPath(id, variant), { headers: this.headers(), signal });
    if (!res.ok) throw new ApiError(res.status, 'not_found', '照片取不到');
    return res.blob();
  }

  /** 用 XHR 上传，为了拿到进度 */
  upload(form: FormData, onProgress?: (fraction: number) => void): Promise<UploadResponse> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', API.photos);
      if (this.token) xhr.setRequestHeader('authorization', `Bearer ${this.token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress?.(e.loaded / e.total);
      };
      xhr.onload = () => {
        let body: unknown = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          // 留空
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(body as UploadResponse);
        else {
          const err = (body ?? {}) as Partial<ApiErrorBody>;
          reject(new ApiError(xhr.status, err.error ?? 'internal', err.message ?? `上传失败（${xhr.status}）`));
        }
      };
      xhr.onerror = () => reject(new ApiError(0, 'network', '网络断了，没传上去'));
      xhr.send(form);
    });
  }

  /** 订阅变更，断线自动重连并补发。返回取消函数 */
  subscribe(fromSeq: number, onChange: (c: Change) => void, onState?: (s: StreamState) => void): () => void {
    let last = fromSeq;
    let stopped = false;
    let ctrl: AbortController | null = null;
    let delay = 1000;
    let wakeUp: (() => void) | null = null;

    const handle = (raw: string) => {
      let event = 'message', data = '';
      for (const line of raw.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += (data ? '\n' : '') + line.slice(5).replace(/^ /, '');
      }
      if (event !== 'change' || !data) return;
      const c = JSON.parse(data) as Change;
      if (c.seq <= last) return;
      last = c.seq;
      onChange(c);
    };

    const run = async () => {
      while (!stopped) {
        ctrl = new AbortController();
        try {
          const res = await fetch(API.events, {
            headers: this.headers({ accept: 'text/event-stream', 'last-event-id': String(last) }),
            signal: ctrl.signal,
            cache: 'no-store',
          });
          if (res.status === 401) {
            onState?.('unauthorized');
            return;
          }
          if (!res.ok || !res.body) throw new Error(`事件流 ${res.status}`);
          onState?.('open');
          delay = 1000;
          const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
          let buf = '';
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += value;
            let i: number;
            while ((i = buf.indexOf('\n\n')) >= 0) {
              handle(buf.slice(0, i));
              buf = buf.slice(i + 2);
            }
          }
        } catch {
          if (stopped) return;
        }
        if (stopped) return;
        onState?.('retrying');
        await new Promise<void>((r) => {
          wakeUp = r;
          setTimeout(r, delay);
        });
        wakeUp = null;
        delay = Math.min(delay * 2, 30_000);
      }
    };

    const online = () => wakeUp?.();
    window.addEventListener('online', online);
    void run();
    return () => {
      stopped = true;
      window.removeEventListener('online', online);
      ctrl?.abort();
      wakeUp?.();
    };
  }
}
